/**
 * AI Provider Selector Component
 * Allows users to select their preferred AI provider and model.
 */

import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { aiProvidersAPI, UserAIPreferences } from '@api/aiProviders'
import { SparklesIcon, CheckIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import clsx from 'clsx'

interface AIProviderSelectorProps {
  compact?: boolean
  showLabel?: boolean
  className?: string
}

const PROVIDER_NAMES: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Claude',
  grok: 'Grok',
}

const PROVIDER_ICONS: Record<string, string> = {
  openai: '🤖',
  anthropic: '🧠',
  grok: '⚡',
}

export const AIProviderSelector: React.FC<AIProviderSelectorProps> = ({
  compact = false,
  showLabel = true,
  className = '',
}) => {
  const queryClient = useQueryClient()
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null)
  const [selectedModel, setSelectedModel] = useState<string | null>(null)

  // Fetch preferences
  const { data: preferences, isLoading } = useQuery({
    queryKey: ['ai-preferences'],
    queryFn: () => aiProvidersAPI.getPreferences(),
  })

  // Update local state when preferences load
  useEffect(() => {
    if (preferences) {
      setSelectedProvider(preferences.provider || null)
      setSelectedModel(preferences.model || null)
    }
  }, [preferences])

  // Update preferences mutation
  const updateMutation = useMutation({
    mutationFn: (data: { provider?: string | null; model?: string | null }) =>
      aiProvidersAPI.updatePreferences(data),
    onSuccess: (data) => {
      queryClient.setQueryData(['ai-preferences'], data)
    },
    onError: (error: any) => {
    },
  })

  const handleProviderChange = (provider: string) => {
    setSelectedProvider(provider)
    // Clear model if switching providers
    setSelectedModel(null)
    
    // Update preference
    updateMutation.mutate({
      provider,
      model: null, // Clear model when switching providers
    })
  }

  const handleModelChange = (model: string) => {
    setSelectedModel(model)
    
    // Update preference
    updateMutation.mutate({
      provider: selectedProvider,
      model,
    })
  }

  if (isLoading) {
    return (
      <div className={clsx('flex items-center gap-2', className)}>
        {showLabel && <span className="text-sm text-gray-600 dark:text-gray-400">Loading...</span>}
      </div>
    )
  }

  if (!preferences || preferences.available_providers.length === 0) {
    return (
      <div className={clsx('flex items-center gap-2', className)}>
        {showLabel && (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            No AI providers available
          </span>
        )}
      </div>
    )
  }

  const currentProvider = selectedProvider || preferences.available_providers[0]
  const availableModels = preferences.provider_models[currentProvider] || []

  if (compact) {
    return (
      <div className={clsx('flex items-center gap-2', className)}>
        <select
          value={currentProvider}
          onChange={(e) => handleProviderChange(e.target.value)}
          disabled={updateMutation.isPending}
          className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {preferences.available_providers.map((provider) => (
            <option key={provider} value={provider}>
              {PROVIDER_ICONS[provider]} {PROVIDER_NAMES[provider] || provider}
            </option>
          ))}
        </select>
        {availableModels.length > 0 && (
          <select
            value={selectedModel || ''}
            onChange={(e) => handleModelChange(e.target.value)}
            disabled={updateMutation.isPending}
            className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <option value="">Default</option>
            {availableModels.map((model) => (
              <option key={model} value={model}>
                {model}
              </option>
            ))}
          </select>
        )}
      </div>
    )
  }

  return (
    <div className={clsx('space-y-4', className)}>
      {showLabel && (
        <div className="flex items-center gap-2">
          <SparklesIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            AI Provider Preferences
          </h3>
        </div>
      )}

      {/* Provider Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          AI Provider
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {preferences.available_providers.map((provider) => {
            const isSelected = selectedProvider === provider
            return (
              <button
                key={provider}
                onClick={() => handleProviderChange(provider)}
                disabled={updateMutation.isPending}
                className={clsx(
                  'flex items-center justify-between px-4 py-3 border-2 rounded-lg transition-all',
                  isSelected
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800',
                  updateMutation.isPending && 'opacity-50 cursor-not-allowed'
                )}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xl">{PROVIDER_ICONS[provider]}</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {PROVIDER_NAMES[provider] || provider}
                  </span>
                </div>
                {isSelected && (
                  <CheckIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* Model Selection */}
      {availableModels.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Model
          </label>
          <select
            value={selectedModel || ''}
            onChange={(e) => handleModelChange(e.target.value)}
            disabled={updateMutation.isPending}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <option value="">Use default model</option>
            {availableModels.map((model) => (
              <option key={model} value={model}>
                {model}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Leave empty to use the default model for the selected provider
          </p>
        </div>
      )}

      {updateMutation.isPending && (
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Updating preferences...
        </div>
      )}
    </div>
  )
}

